'use client';

import {
  ReactNode, Children, useEffect, useRef, useState,
} from 'react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { Button } from '@components/common';
import { cn } from '@utils/cn';

interface FloatingNavBarOverflowMenuProps {
  children: ReactNode;
  /**
   * When false, hides both primary items and the More button (used by the
   * CSS-grid invisible-spacer trick while an inline search is open).
   */
  isVisible?: boolean;
  /** When true, renders all children on mobile without the overflow popover. */
  disableOverflow?: boolean;
}

/**
 * @description Renders nav-bar items inline on desktop (≥ lg).
 * On mobile, only the first 2 children are shown in the pill; the rest are
 * accessible via a "⋯" More button that opens a small popover above the pill.
 */
export const FloatingNavBarOverflowMenu = ({
  children,
  isVisible = true,
  disableOverflow = false,
}: FloatingNavBarOverflowMenuProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // Detect mobile breakpoint, update on resize
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Close popover on outside click
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close popover when switching to desktop
  useEffect(() => {
    if (!isMobile) setIsOpen(false);
  }, [isMobile]);

  const allItems = Children.toArray(children);

  // When overflow is disabled, render all items directly (compact mobile layout).
  // On mobile, items wrap to a second row and are centered when they exceed the available width.
  if (disableOverflow) {
    const visClass = isVisible ? '' : 'invisible pointer-events-none select-none';
    return (
      <div className={cn('flex flex-wrap items-center justify-center gap-1.5 lg:flex-nowrap lg:gap-5', visClass)}>
        {allItems}
      </div>
    );
  }

  const primaryItems = allItems.slice(0, 2);
  const overflowItems = allItems.slice(2);
  const hasOverflow = isMobile && overflowItems.length > 0;

  const visibilityClass = isVisible ? '' : 'invisible pointer-events-none select-none';

  return (
    <div className={cn('flex items-center gap-5', visibilityClass)}>
      {/* Primary items — always shown */}
      {isMobile ? primaryItems : allItems}

      {/* More button + popover — mobile only, when there are overflow items */}
      {hasOverflow && (
        <div ref={moreRef} className="relative">
          {/* Popover panel — appears above the More button */}
          {isOpen && (
            <div className="absolute bottom-full right-0 mb-2 flex min-w-[160px] flex-col gap-2 rounded-2xl bg-transparent p-3">
              {overflowItems.map((item, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} onClick={() => setIsOpen(false)} role="none">
                  {item}
                </div>
              ))}
            </div>
          )}

          <Button
            type="button"
            styling="solid"
            color="light"
            className="!h-11 !w-11 !rounded-2xl !p-0"
            onClick={() => setIsOpen((prev) => !prev)}
            aria-label="More options"
            aria-expanded={isOpen}
            aria-haspopup="true"
          >
            <EllipsisHorizontalIcon className="size-5" />
          </Button>
        </div>
      )}
    </div>
  );
};
